import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { FaPlus } from "react-icons/fa6";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { Formik, Form } from "formik";
import { TableLoader } from "../../../Common/ShimmerLoader";
import MUITable from "../../../Common/MUITable";
import isGHMC from "../../../Common/helper/helpar";
import { ExportSlaExceptionRule } from "./ExportSlaExceptionRule";
import AddEditSlaExceptionRuleForm from "./AddEditSlaExceptionRuleForm";
import { useSlaExceptionRuleStore } from "./store/useSlaExceptionRuleStore";
import { StatusBadge } from "../../Rfid/utils/statusBadge";

const formatDisplayDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDisplayDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SlaExceptionRuleRootComponent = () => {
  const isProd = isGHMC();
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [exportData, setExportData] = useState([]);

  const {
    filteredSlaExceptionRules,
    filters,
    isLoading,
    pagination,
    fetchSlaExceptionRules,
    fetchActionCategories,
    fetchAllSlaExceptionRulesForExport,
    resetFilters,
  } = useSlaExceptionRuleStore();

  useEffect(() => {
    fetchActionCategories();
    fetchSlaExceptionRules();
  }, [fetchActionCategories, fetchSlaExceptionRules]);

  useEffect(() => {
    setExportData(filteredSlaExceptionRules);
  }, [filteredSlaExceptionRules]);

  const handleExportDataLoad = async () => {
    const data = await fetchAllSlaExceptionRulesForExport();
    setExportData(data);
    return data;
  };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditRow(row);
    setShowModal(true);
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditRow(null);
    setShowModal(true);
  };

  const handlePageChange = (_event, newPage) => {
    fetchSlaExceptionRules({
      currentPage: newPage + 1,
    });
  };

  const handleRowsPerPageChange = (event) => {
    const nextPageSize = parseInt(event.target.value, 10);
    fetchSlaExceptionRules({
      currentPage: 1,
      pageSize: nextPageSize,
    });
  };

  const initialFilterValues = {
    searchTerm: filters.searchTerm,
  };

  const slaExceptionRuleColumns = [
    { label: "Zone", field: "zoneName", width: "180px" },
    {
      label: "Ward",
      width: "300px",
      render: (row) => row.wardName || row.wardNo || "N/A",
    },
    {
      label: "Action Category",
      width: "200px",
      render: (row) =>
        row.actionCategoryName || row.actionCategory || "N/A",
    },
    { label: "Remarks", field: "remarks", width: "300px"},
    {
      label: "Valid From",
      width: "140px",
      render: (row) => formatDisplayDate(row.validFrom),
    },
    {
      label: "Valid Till",
      width: "140px",
      render: (row) => formatDisplayDate(row.validTill || row.validTo),
    },
    {
      label: "Status",
      render: (row) => <StatusBadge isActive={row.isActive} compact />,
    },
    {
      label: "Last Modified On",
      width: "180px",
      render: (row) => formatDisplayDateTime(row.lastModifiedOn),
    },
    {
      label: "Actions",
      render: (row) => (
        <button
          type="button"
          className={isProd ? "edit-ghmc" : "edit-swm"}
          onClick={() => handleEdit(row)}
          aria-label={`Edit ${row.actionCategoryName || row.actionCategory}`}
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              <h5 className="mb-1 fw-semibold">SLA Exception Rule Information</h5>
              <p className="text-muted mb-0">
                Manage temporary SLA exemptions for holidays, breakdowns, weather
                disruptions, and special operational conditions.
              </p>
            </div>
            <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
              <button
                type="button"
                className="button-container"
                onClick={handleAdd}
                style={{ whiteSpace: "nowrap" }}
              >
                <FaPlus size={30} className="me-1" /> Add SLA Exception Rule
              </button>
              <ExportSlaExceptionRule
                slaExceptionRuleData={exportData}
                loadExportData={handleExportDataLoad}
              />
            </div>
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <Formik
                initialValues={initialFilterValues}
                enableReinitialize
                onSubmit={(values) => {
                  fetchSlaExceptionRules({
                    currentPage: 1,
                    searchTerm: values.searchTerm?.trim() ?? "",
                  });
                }}
              >
                {({ values, setFieldValue, resetForm, handleSubmit }) => (
                  <>
                    <Form onSubmit={handleSubmit}>
                      <div className="d-flex align-items-end justify-content-between mb-3">
                        <div style={{ minWidth: "280px" }}>
                          <label className="form-label">Search</label>
                          <input
                            type="text"
                            name="searchTerm"
                            value={values.searchTerm}
                            onChange={(e) => setFieldValue("searchTerm", e.target.value)}
                            placeholder="Search Action Category / Remarks"
                            className="form-control bg-white"
                          />
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            type="submit"
                            style={{
                              backgroundColor: "#003366",
                              color: "#FFFFFF",
                              border: "none",
                              fontWeight: 600,
                              padding: "8px 24px",
                              borderRadius: "6px",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            APPLY FILTERS
                          </button>
                          <button
                            type="button"
                            style={{
                              backgroundColor: "#FDE8E8",
                              color: "#8B1A1A",
                              border: "none",
                              fontWeight: 600,
                              padding: "8px 24px",
                              borderRadius: "6px",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                            onClick={() => {
                              resetForm();
                              resetFilters();
                            }}
                          >
                            RESET
                          </button>
                        </div>
                      </div>
                    </Form>

                    <div>
                      <div className="top-header text-center">
                        <span>SLA Exception Rule List</span>
                      </div>
                      <Box
                        sx={{
                          "& .MuiTableBody-root .MuiTableCell-root": {
                            padding: "4px 10px",
                          },
                        }}
                      >
                        <MUITable loading={isLoading}
                          needSRNumber
                          rows={filteredSlaExceptionRules}
                          columns={slaExceptionRuleColumns}
                          serverPagination
                          totalCount={pagination.totalCount}
                          page={Math.max(0, pagination.currentPage - 1)}
                          rowsPerPage={pagination.pageSize}
                          onPageChange={handlePageChange}
                          onRowsPerPageChange={handleRowsPerPageChange}
                          rowsPerPageOptions={[10, 100, 10000, 50000]}
                        />
                      </Box>
                    </div>
                  </>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <AddEditSlaExceptionRuleForm
        showModal={showModal}
        setShowModal={setShowModal}
        editRow={editRow}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default SlaExceptionRuleRootComponent;
